import { Injectable } from '@angular/core';
import { ColDef } from 'ag-grid-community';
import {
  AccessPermissionService,
  defaultColumn,
  DELETE_RECORD_LABEL,
  grid_widths_map,
} from '@piqet-angular/shared';
import { SwalService } from 'libs/shared/src/lib/service/swal.service';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ProductEolDataGridService {
  deleteProductEOL = new Subject<[any]>();
  deleteProductEOLData$ = this.deleteProductEOL.asObservable();

  editProductEOL = new Subject<[any, any]>();
  editProductEOLData$ = this.editProductEOL.asObservable();

  constructor(private swalService: SwalService, private accessService: AccessPermissionService) {}

  productEOLGrid(apiResponse?: any): ColDef[] {
    const colDef: ColDef[] = [];
    const disposalColumnCount =
      apiResponse?.disposalColumns && Array.isArray(apiResponse.disposalColumns)
        ? apiResponse.disposalColumns.length
        : 0;
    // First column - Product Name (dynamic header)
    const productColumnWidth =
      disposalColumnCount === 1
        ? 1130
        : disposalColumnCount === 2
        ? 880
        : disposalColumnCount === 3
        ? 630
        : disposalColumnCount === 4
        ? 380
        : 300;
    const productLabel = apiResponse?.productLabel ?? 'Product Name';
    colDef.push({
      ...defaultColumn,
      colId: 'ProductName',
      headerName: productLabel,
      field: 'productName',
      suppressColumnsToolPanel: true,
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: false,
      lockPosition: true,
      width: productColumnWidth, // Dynamic width based on disposal count
      minWidth: productColumnWidth,
      maxWidth: productColumnWidth,
      resizable: false,
      pinned: 'left',
      cellRenderer: (params: any) => {
        const cellValue = params.value;
        const cellElement = document.createElement('span');
        cellElement.innerHTML = `${cellValue}`;
        return cellElement;
      },
    });

    // Dynamic disposal columns from API
    if (apiResponse?.disposalColumns && Array.isArray(apiResponse.disposalColumns)) {
      apiResponse.disposalColumns.forEach((column: any, index: number) => {
        colDef.push({
          ...defaultColumn,
          colId: `disposal_${column.id ?? index}`,
          headerName: column.headerName,
          field: `disposal_${column.id ?? index}`,
          lockVisible: true,
          resizable: false,
          lockPosition: true,
          wrapHeaderText: true,
          autoHeaderHeight: true,
          wrapText: true,
          minWidth: grid_widths_map.sm_column_lg,
          maxWidth: grid_widths_map.sm_column_lg,
          cellRenderer: (params: any) => {
            const value = params.value;
            const displayValue = value !== null && value !== undefined ? value : '';

            const cellContainer = document.createElement('div');
            cellContainer.style.display = 'flex';
            cellContainer.style.alignItems = 'center';
            cellContainer.style.justifyContent = 'space-between';

            const valueSpan = document.createElement('span');
            valueSpan.innerHTML = displayValue;

            const pencilIcon = document.createElement('span');
            pencilIcon.innerHTML =
              '<i style="bottom:13px;margin-right: 24px;" class="fa fa-pencil icon-opacity" aria-hidden="true"></i>';
            pencilIcon.style.cursor = 'pointer';
            pencilIcon.style.marginLeft = '10px';
            pencilIcon.title = 'Edit';

            pencilIcon.addEventListener('click', e => {
              e.stopPropagation();
              // Pass the disposal column id for editing
              this.editProductEOL.next([column.id, 'Review product EOL']);
            });

            const deleteIcon = document.createElement('span');
            deleteIcon.innerHTML =
              '<i style="bottom:13px" class="fa fa-trash icon-opacity" aria-hidden="true"></i>';
            deleteIcon.style.cursor = 'pointer';
            deleteIcon.style.marginLeft = '10px';
            deleteIcon.title = 'Delete';

            deleteIcon.addEventListener('click', e => {
              e.stopPropagation();
              // Pass the disposal column id for deletion
              this.deleteConfirmation({ id: column.id });
            });

            cellContainer.appendChild(valueSpan);
            cellContainer.appendChild(pencilIcon);
            cellContainer.appendChild(deleteIcon);

            return cellContainer;
          },
        });
      });
    }

    return colDef;
  }

  transformDataForGrid(apiResponse: any): any[] {
    if (!apiResponse) {
      return [];
    }

    const rowData: any = {
      productName: apiResponse.productName ?? '',
      id: apiResponse.id ?? 0,
    };

    if (apiResponse.disposalColumns && Array.isArray(apiResponse.disposalColumns)) {
      apiResponse.disposalColumns.forEach((column: any) => {
        rowData[`disposal_${column.id}`] = column.value;
      });
    }

    return [rowData];
  }

  deleteConfirmation(data: any) {
    this.swalService.confirm('Are you sure?', DELETE_RECORD_LABEL).then(confirmed => {
      if (confirmed) {
        this.deleteProductEOL.next([data]);
      }
    });
  }
}
